import { ComponentPropsWithoutRef, ReactNode } from 'react'

import { cn } from '@sparkai/utils'

import * as Logos from './Logos'

export interface NoDataPlaceholderProps extends ComponentPropsWithoutRef<'div'> {
  children?: ReactNode
  title?: string
  titleHidden?: boolean
  text?: string | ReactNode
  variant: 'xs' | 's' | 'm' | 'l' | 'full' | 'horizontal'
  containerClassName?: string
  textClassName?: string
  titleClassName?: string
  'data-testid'?: string
}

const logoMap = {
  xs: Logos.LogoGrayExtraSmall,
  s: Logos.LogoGraySmall,
  m: Logos.LogoGrayMedium,
  l: Logos.LogoGrayLarge,
  full: Logos.LogoGrayLarge,
  horizontal: Logos.LogoGrayExtraSmall,
}

const containerVariantClasses = {
  xs: 'py-2 text-xs w-[300] lg:w-[352px] max-h-[155px]',
  s: 'py-4 text-sm w-[300] lg:w-[352px] max-h-[269px]',
  m: 'py-4 text-sm w-[300] lg:w-[352px] max-h-[300px]',
  l: 'py-4 px-20 text-sm w-[512px] max-h-[329px]',
  full: 'py-4 px-20 text-sm',
  horizontal: 'py-4 px-20 text-sm',
}
const bodyVariantClasses = {
  xs: 'gap-4',
  s: 'gap-[15px]',
  m: 'gap-[26px]',
  l: 'gap-[29px]',
  full: 'gap-[29px]',
  horizontal: 'gap-[29px]',
}

export function NoDataPlaceholder({
  children,
  title = 'No data found',
  text,
  variant,
  containerClassName,
  textClassName,
  titleClassName,
  titleHidden,
  ...props
}: Readonly<NoDataPlaceholderProps>) {
  const Logo = logoMap[variant]
  const containerClasses = containerVariantClasses[variant]
  const bodyClasses = bodyVariantClasses[variant]

  return (
    <div
      {...props}
      className={cn('NoDataPlaceholder text-gray-500', containerClasses, containerClassName)}
      data-testid={props?.['data-testid'] ?? 'no-data-placeholder'}
    >
      <div
        className={cn(
          'NoDataPlaceholder__Body box flex h-full w-full flex-col items-center justify-center',
          bodyClasses,
          variant === 'horizontal' && 'flex-row'
        )}
      >
        <Logo />
        <h4
          className={cn(
            'text-center text-black lg:text-left',
            {
              'text-base': variant === 'xs',
              'text-xl': variant !== 'xs',
            },
            titleClassName
          )}
        >
          {!titleHidden && title}
        </h4>
        <div className={cn('text-center', textClassName)}>{text}</div>
        {children}
      </div>
    </div>
  )
}
